import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { safeLocalStorageRead, safeLocalStorageWrite } from '@/lib/storage/localStorage';

interface UseResizablePanelOptions {
  minWidth: number;
  maxWidth: number;
  initialWidth: number;
  /** Pixels to grow/shrink per arrow-key press. */
  step?: number;
  /** Which edge of the panel the handle sits on. Inverts drag/key direction so dragging away from the panel grows it. Defaults to 'right'. */
  resizeFrom?: 'left' | 'right';
  /** If set, the user-chosen width is persisted under this localStorage key and rehydrated on mount. */
  storageKey?: string;
}

export interface ResizeSeparatorProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-valuemin': number;
  'aria-valuemax': number;
  'aria-valuenow': number;
  tabIndex: number;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

interface UseResizablePanelResult {
  width: number;
  setWidth: (width: number) => void;
  /** Attach to the resized element, and keep applying `width` as its style — React owns it between drags. */
  panelRef: RefObject<HTMLDivElement>;
  /** True while the handle is hovered or dragged — drives the accent styling. */
  isActive: boolean;
  /** True once the user has resized via drag or keyboard (not via programmatic `setWidth`). */
  hasUserResized: boolean;
  /** True when the initial width was rehydrated from localStorage (storageKey was set and had a valid saved value). */
  wasRestoredFromStorage: boolean;
  separatorProps: ResizeSeparatorProps;
}

const DEFAULT_STEP = 16;

// `document.body` is not guaranteed to exist — a null body crashed this in production.
const setBodyUserSelect = (value: string) => {
  if (document.body) {
    document.body.style.userSelect = value;
  }
};

/** Drag- and keyboard-resizable panel width (WAI-ARIA window-splitter); a drag commits on pointerup. */
export const useResizablePanel = ({
  minWidth,
  maxWidth,
  initialWidth,
  step = DEFAULT_STEP,
  resizeFrom = 'right',
  storageKey,
}: UseResizablePanelOptions): UseResizablePanelResult => {
  const directionSign = resizeFrom === 'left' ? -1 : 1;

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [minWidth, maxWidth]
  );

  const storedWidth = useState(() => {
    if (!storageKey) {
      return null;
    }
    const raw = safeLocalStorageRead(storageKey);
    // Guard against the empty key (null) and empty string — Number() coerces both to 0.
    if (raw == null || raw === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  })[0];

  // storedWidth is read once at mount and never changes, so this is just a derived const.
  const wasRestoredFromStorage = storedWidth !== null;

  const [width, setWidthState] = useState(() =>
    storedWidth === null ? initialWidth : clamp(storedWidth)
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(width);
  // Tracks an in-flight drag so we can tear it down if the component unmounts mid-drag.
  const dragAbortRef = useRef<AbortController | null>(null);
  // A drag owns widthRef; a render in the middle of one must not roll it back to the state value.
  if (!dragAbortRef.current) {
    widthRef.current = width;
  }

  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasUserResized, setHasUserResized] = useState(false);

  const persist = useCallback(
    (next: number) => {
      if (storageKey) {
        safeLocalStorageWrite(storageKey, String(next));
      }
    },
    [storageKey]
  );

  const setWidth = useCallback((next: number) => setWidthState(clamp(next)), [clamp]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const startX = e.clientX;
      const startWidth = widthRef.current;
      // `width` commits on pointerup, so the node keeps `aria-valuenow` honest during a drag.
      const separator = e.currentTarget as HTMLElement | null;
      setBodyUserSelect('none');

      const controller = new AbortController();
      dragAbortRef.current = controller;

      const onPointerMove = (ev: PointerEvent) => {
        const next = clamp(startWidth + directionSign * (ev.clientX - startX));
        widthRef.current = next;
        setHasUserResized(true);
        // The node, not state: a drag re-rendering the panel redraws its whole header per event.
        if (panelRef.current) {
          panelRef.current.style.width = `${next}px`;
        }
        separator?.setAttribute('aria-valuenow', String(next));
      };
      // `pointercancel` and lost capture end a drag too; miss them and the abort ref sticks.
      const endDrag = () => {
        setIsDragging(false);
        setBodyUserSelect('');
        controller.abort();
        dragAbortRef.current = null;
        setWidthState(widthRef.current);
        persist(widthRef.current);
      };

      document.addEventListener('pointermove', onPointerMove, { signal: controller.signal });
      document.addEventListener('pointerup', endDrag, { signal: controller.signal });
      document.addEventListener('pointercancel', endDrag, { signal: controller.signal });
      separator?.addEventListener('lostpointercapture', endDrag, { signal: controller.signal });
    },
    [clamp, directionSign, persist]
  );

  // Safety net: if the panel unmounts mid-drag, pointerup never fires — tear down the
  // listeners and restore text selection so the whole page isn't left unselectable.
  useEffect(
    () => () => {
      dragAbortRef.current?.abort();
      setBodyUserSelect('');
    },
    []
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const nextByKey: Record<string, number> = {
        ArrowLeft: widthRef.current - step * directionSign,
        ArrowRight: widthRef.current + step * directionSign,
        Home: minWidth,
        End: maxWidth,
      };
      const next = nextByKey[e.key];
      if (next === undefined) {
        return;
      }
      e.preventDefault();
      setHasUserResized(true);
      const clamped = clamp(next);
      setWidthState(clamped);
      persist(clamped);
    },
    [clamp, step, minWidth, maxWidth, directionSign, persist]
  );

  return {
    width,
    setWidth,
    panelRef,
    isActive: isHovered || isDragging,
    hasUserResized,
    wasRestoredFromStorage,
    separatorProps: {
      role: 'separator',
      'aria-orientation': 'vertical',
      'aria-valuemin': minWidth,
      'aria-valuemax': maxWidth,
      'aria-valuenow': width,
      tabIndex: 0,
      onPointerDown,
      onPointerEnter: () => setIsHovered(true),
      onPointerLeave: () => setIsHovered(false),
      onKeyDown,
    },
  };
};
